import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import DOMPurify from 'dompurify'

import { Dialog, snackbar } from 'components'
import { useApiCall } from 'hooks'
import { ERRORS } from 'consts'

import { updateMembershipPrizesOrder } from '../api'
import { Membership, UpdateMembershipPrizeOrderData } from '../types'

interface SortableItemProps {
  id: number
  title: string
  index: number
}

const SortableItem = ({ id, title, index }: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `prize_${id}`,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? '#e78923' : 'grey',
    padding: 16,
    margin: '0 0 8px 0',
    userSelect: 'none' as const,
  }

  // eslint-disable-next-line no-shadow
  const getOrderedTitle = (title: string, index: number) => {
    if (title.startsWith('<p>')) {
      const strippedTitle = title.replace('<p>', '')
      return `<p>${index + 1}. ${strippedTitle}`
    }
    return `${index + 1}. ${title}`
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(getOrderedTitle(title, index)),
      }}
    />
  )
}

interface CreateMembershipsDialogProps {
  membership: Membership
  handleClose: () => void
  onDone: (newMembership: Membership) => void
}

const OrderMembershipPrizesDialog: React.FC<CreateMembershipsDialogProps> = ({
  membership,
  handleClose,
  onDone,
}) => {
  const [prizes, setPrizes] = useState(membership.prizes)

  const [updateMembershipApi] = useApiCall<UpdateMembershipPrizeOrderData, void>(
    updateMembershipPrizesOrder,
  )

  useEffect(() => {
    if (prizes) {
      const sortedPrizes = [...prizes].sort((a, b) => a.order - b.order)
      setPrizes(sortedPrizes)
    }
  }, [prizes])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || !prizes) return

    if (active.id !== over.id) {
      const oldIndex = prizes.findIndex(p => `prize_${p.id}` === active.id)
      const newIndex = prizes.findIndex(p => `prize_${p.id}` === over.id)

      setPrizes(arrayMove(prizes, oldIndex, newIndex))
    }
  }

  const handleSubmit = async () => {
    try {
      if (!prizes) return
      await updateMembershipApi({ id: membership.id, prizes })
      onDone({
        ...membership,
        prizes,
      })
    } catch (err) {
      console.error(err)
      snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
    }
  }

  return (
    <Dialog
      title="Ordenar premios"
      isOpen
      showActions
      onCancel={handleClose}
      okButtonText="Guardar"
      onAccept={handleSubmit}
      contentStyle={{ minWidth: 500 }}
    >
      <Grid container spacing={3} justify="center" alignItems="center">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={prizes?.map(p => `prize_${p.id}`) || []}
            strategy={verticalListSortingStrategy}
          >
            {prizes?.map((prize, index) => (
              <SortableItem key={prize.id} id={prize.id} title={prize.title} index={index} />
            ))}
          </SortableContext>
        </DndContext>
      </Grid>
    </Dialog>
  )
}

export { OrderMembershipPrizesDialog }
